import React from "react";
import Box from "@mui/joy/Box";
import Button from "@mui/joy/Button";
import Chip from "@mui/joy/Chip";
import IconButton from "@mui/joy/IconButton";
import Modal from "@mui/joy/Modal";
import ModalClose from "@mui/joy/ModalClose";
import ModalDialog from "@mui/joy/ModalDialog";
import Sheet from "@mui/joy/Sheet";
import Stack from "@mui/joy/Stack";
import Table from "@mui/joy/Table";
import Tooltip from "@mui/joy/Tooltip";
import Typography from "@mui/joy/Typography";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import FormHelperText from "@mui/joy/FormHelperText";
import Input from "@mui/joy/Input";
import Grid from "@mui/joy/Grid";
import EditIcon from "@mui/icons-material/Edit";
import SyncIcon from "@mui/icons-material/Sync";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { NotificationService } from "../../../common/notification";
import { formatUtcDateTime } from "../../../common/dateTime";
import { Game } from "../../../models/games";
import {
    AdminExternalMarketInfo,
    AdminExternalProviderInfo,
    UpdateExternalMarketRequest,
} from "../../../models/externalMarkets";
import { formatInt } from "../../bids/analytics/format";
import { extMarketSyncApi, extMarketToggleApi, extMarketUpdateApi } from "../externalMarketsApi";
import { formatPercent, formatUsd, parseOptionalNumber, ReasonDialog, resolveGameName } from "../components/shared";

type Props = {
    provider: AdminExternalProviderInfo;
    games: Game[];
    onChanged: () => void;
};

export function MarketsTab(props: Props) {
    const { provider, games, onChanged } = props;
    const [toggle, setToggle] = React.useState<AdminExternalMarketInfo | null>(null);
    const [edit, setEdit] = React.useState<AdminExternalMarketInfo | null>(null);
    const [syncing, setSyncing] = React.useState<number | null>(null);

    const sync = async (m: AdminExternalMarketInfo) => {
        setSyncing(m.gameId);
        try {
            const res = await extMarketSyncApi(provider.key, m.gameId);
            if (res.result) {
                NotificationService.alertSuccess("Синхронизация витрины поставлена в очередь");
            } else {
                NotificationService.alertError(res.error ?? "Не удалось запустить синхронизацию");
            }
        } finally {
            setSyncing(null);
        }
    };

    if (provider.markets.length === 0) {
        return <Typography level="body-sm">У поставщика нет связок с играми.</Typography>;
    }

    return (
        <>
            <Sheet variant="outlined" sx={{ borderRadius: "sm", overflow: "auto" }}>
                <Table size="sm" hoverRow sx={{ minWidth: 1100 }}>
                    <thead>
                        <tr>
                            <th style={{ width: 150 }}>Игра</th>
                            <th style={{ width: 200 }}>Состояние</th>
                            <th style={{ textAlign: "right" }}>На витрине</th>
                            <th style={{ textAlign: "right" }}>Провалы за 24ч</th>
                            <th style={{ textAlign: "right" }}>Провалов подряд</th>
                            <th>Допуски</th>
                            <th>Лимиты</th>
                            <th>Последняя синхронизация</th>
                            <th style={{ width: 130 }}>Действия</th>
                        </tr>
                    </thead>
                    <tbody>
                        {provider.markets.map((m) => (
                            <tr key={m.id}>
                                <td>
                                    <Typography level="title-sm">{resolveGameName(games, m.gameId)}</Typography>
                                    <Typography level="body-xs">#{m.gameId}</Typography>
                                </td>
                                <td>
                                    <Chip size="sm" variant="soft" color={m.isActive ? "success" : "danger"}>
                                        {m.isActive ? "Включена" : "Выключена"}
                                    </Chip>
                                    {!m.isActive && (
                                        <Typography level="body-xs" sx={{ mt: 0.5 }}>
                                            {m.disabledReason || "без причины"}
                                            <br />
                                            {m.disabledBy ?? "—"}, {formatUtcDateTime(m.disabledAt)}
                                        </Typography>
                                    )}
                                    {m.unavailableSince && (
                                        <Typography level="body-xs" color="warning" sx={{ mt: 0.5 }}>
                                            API недоступно с {formatUtcDateTime(m.unavailableSince)}
                                        </Typography>
                                    )}
                                </td>
                                <td style={{ textAlign: "right" }}>{formatInt(m.activeListings)}</td>
                                <td style={{ textAlign: "right" }}>
                                    <Typography level="body-sm" color={m.failedPurchases24h > 0 ? "danger" : undefined}>
                                        {formatInt(m.failedPurchases24h)}
                                    </Typography>
                                </td>
                                <td style={{ textAlign: "right" }}>
                                    {m.consecutiveFailures}
                                    {m.autoDisableFailuresInRow > 0 ? ` / ${m.autoDisableFailuresInRow}` : ""}
                                </td>
                                <td>
                                    <Typography level="body-xs">Цена: до +{formatPercent(m.maxPriceDeviationPercent)}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">Мин. баланс: {formatUsd(m.minBalanceUsd)}</Typography>
                                </td>
                                <td>
                                    <Typography level="body-xs">{formatUtcDateTime(m.lastSyncAt)}</Typography>
                                    {m.lastSyncResult && (
                                        <Tooltip title={m.lastSyncResult} variant="soft" sx={{ maxWidth: 480 }}>
                                            <Typography
                                                level="body-xs"
                                                color={m.lastSyncResult.startsWith("error") ? "danger" : "neutral"}
                                                sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                                            >
                                                {m.lastSyncResult}
                                            </Typography>
                                        </Tooltip>
                                    )}
                                </td>
                                <td>
                                    <Stack direction="row" spacing={0.5}>
                                        <Tooltip title={m.isActive ? "Выключить связку" : "Включить связку"}>
                                            <IconButton
                                                size="sm"
                                                variant="outlined"
                                                color={m.isActive ? "danger" : "success"}
                                                onClick={() => setToggle(m)}
                                            >
                                                <PowerSettingsNewIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title="Допуски и лимиты">
                                            <IconButton size="sm" variant="outlined" onClick={() => setEdit(m)}>
                                                <EditIcon />
                                            </IconButton>
                                        </Tooltip>
                                        <Tooltip title={m.isActive ? "Синхронизировать витрину сейчас" : "Связка выключена"}>
                                            <span>
                                                <IconButton
                                                    size="sm"
                                                    variant="outlined"
                                                    disabled={!m.isActive}
                                                    loading={syncing === m.gameId}
                                                    onClick={() => sync(m)}
                                                >
                                                    <SyncIcon />
                                                </IconButton>
                                            </span>
                                        </Tooltip>
                                    </Stack>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            </Sheet>

            <ReasonDialog
                open={toggle !== null}
                title={toggle?.isActive ? "Выключить связку" : "Включить связку"}
                description={
                    toggle?.isActive
                        ? `Предметы ${provider.name} по игре ${resolveGameName(games, toggle?.gameId)} будут поставлены на паузу, новые покупки невозможны.`
                        : `Предметы с паузы вернутся в продажу и сразу запустится синхронизация витрины.`
                }
                confirmLabel={toggle?.isActive ? "Выключить" : "Включить"}
                confirmColor={toggle?.isActive ? "danger" : "success"}
                reasonRequired
                onClose={() => setToggle(null)}
                onConfirm={async (reason) => {
                    if (!toggle) {
                        return;
                    }
                    const res = await extMarketToggleApi(provider.key, toggle.gameId, !toggle.isActive, reason);
                    if (res.result) {
                        NotificationService.alertSuccess(toggle.isActive ? "Связка выключена" : "Связка включена");
                        setToggle(null);
                        onChanged();
                    } else {
                        NotificationService.alertError(res.error ?? "Не удалось изменить состояние связки");
                    }
                }}
            />

            <MarketEditDialog
                market={edit}
                title={edit ? `${provider.name}: ${resolveGameName(games, edit.gameId)}` : ""}
                onClose={() => setEdit(null)}
                onSave={async (request) => {
                    if (!edit) {
                        return;
                    }
                    const res = await extMarketUpdateApi(provider.key, edit.gameId, request);
                    if (res.result) {
                        NotificationService.alertSuccess("Настройки связки сохранены");
                        setEdit(null);
                        onChanged();
                    } else {
                        NotificationService.alertError(res.error ?? "Не удалось сохранить настройки связки");
                    }
                }}
            />
        </>
    );
}

type FieldKey = keyof UpdateExternalMarketRequest;

const FIELDS: { key: FieldKey; label: string; hint: string; integer?: boolean; wide?: boolean }[] = [
    {
        key: "maxPriceDeviationPercent",
        label: "Допуск цены при выкупе, %",
        wide: true,
        hint: [
            "Пока предмет висит на витрине, цена у поставщика и курс доллара могут вырасти. Допуск — насколько выкуп " +
                "может выйти дороже себестоимости, чтобы мы всё равно купили.",
            "Считаем в рублях: цена у поставщика в $ × курс в момент покупки.",
            "Пример: себестоимость 100 ₽, допуск 10% → покупаем, если выкуп обойдётся не дороже 110 ₽. Дороже — заказ " +
                "отменяется, покупателю возвращаются деньги.",
            "Больше допуск — меньше отмен, но меньше прибыль. Допуск выше наценки может привести к продаже в минус.",
            "На балансе аккаунта поставщика должна быть себестоимость плюс этот запас.",
        ].join("\n"),
    },
    { key: "minBalanceUsd", label: "Мин. баланс, USD", hint: "Ниже — алерт в Slack, витрина не снимается" },
    {
        key: "autoDisableFailuresInRow",
        label: "Выключать после провалов подряд",
        hint: "0 — не выключать автоматически",
        integer: true,
    },
    {
        key: "autoDisableUnavailableMinutes",
        label: "Выключать после недоступности API, мин",
        hint: "0 — не выключать автоматически",
        integer: true,
    },
];

function MarketEditDialog(props: {
    market: AdminExternalMarketInfo | null;
    title: string;
    onClose: () => void;
    onSave: (request: UpdateExternalMarketRequest) => Promise<void>;
}) {
    const { market, title, onClose, onSave } = props;
    const [values, setValues] = React.useState<Record<FieldKey, string>>({} as Record<FieldKey, string>);
    const [saving, setSaving] = React.useState(false);

    React.useEffect(() => {
        if (market) {
            const next = {} as Record<FieldKey, string>;
            FIELDS.forEach((f) => (next[f.key] = String(market[f.key] ?? "")));
            setValues(next);
        }
    }, [market]);

    const errors = FIELDS.filter((f) => {
        const n = parseOptionalNumber(values[f.key] ?? "");
        if (n === undefined || n < 0) {
            return true;
        }
        return !!f.integer && !Number.isInteger(n);
    }).map((f) => f.key);

    return (
        <Modal open={market !== null} onClose={() => !saving && onClose()}>
            <ModalDialog sx={{ width: { xs: "100%", sm: 640 } }}>
                <ModalClose disabled={saving} />
                <Typography level="h4">{title}</Typography>
                <Grid container spacing={1.5}>
                    {FIELDS.map((f) => (
                        <Grid key={f.key} xs={12} sm={f.wide ? 12 : 6}>
                            <FormControl size="sm" error={errors.includes(f.key)}>
                                <FormLabel>{f.label}</FormLabel>
                                <Input
                                    value={values[f.key] ?? ""}
                                    onChange={(e) => setValues({ ...values, [f.key]: e.target.value })}
                                />
                                <FormHelperText sx={{ whiteSpace: "pre-line" }}>{f.hint}</FormHelperText>
                            </FormControl>
                        </Grid>
                    ))}
                </Grid>
                <Box sx={{ display: "flex", gap: 1, justifyContent: "flex-end" }}>
                    <Button variant="plain" color="neutral" disabled={saving} onClick={onClose}>
                        Отмена
                    </Button>
                    <Button
                        loading={saving}
                        disabled={errors.length > 0}
                        onClick={async () => {
                            const request = {} as UpdateExternalMarketRequest;
                            FIELDS.forEach((f) => (request[f.key] = parseOptionalNumber(values[f.key]) ?? 0));
                            setSaving(true);
                            try {
                                await onSave(request);
                            } finally {
                                setSaving(false);
                            }
                        }}
                    >
                        Сохранить
                    </Button>
                </Box>
            </ModalDialog>
        </Modal>
    );
}
